import React from 'react';
import { motion } from 'framer-motion';
import { DocumentTextIcon, DeviceTabletIcon, ChartPieIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import InteractiveReport from '../components/reports/InteractiveReport';

const FEATURE_TITLE = "Interactive Reporting";
const FEATURE_DESCRIPTION = "Modern, tabbed interface with interactive charts, expandable sections, and mobile-friendly design for accessing complex information anywhere.";

const InteractiveReportingPage: React.FC = () => {
  return (
    <div className="py-16 bg-background">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1 
            className="text-4xl font-extrabold text-text tracking-tight sm:text-5xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {FEATURE_TITLE}
          </motion.h1>
          <motion.p 
            className="mt-4 text-xl text-text max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {FEATURE_DESCRIPTION}
          </motion.p>
        </div>

        <div className="mt-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Intuitive Interface</h3>
              <p className="text-text">
                Our modern tabbed interface allows clinicians to navigate complex information 
                with ease, focusing on the specific data they need at any moment.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Organized tabbed navigation</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Expandable/collapsible sections</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Customizable dashboard views</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <ChartPieIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Dynamic Visualization</h3>
              <p className="text-text">
                Our interactive charts and data visualizations help clinicians understand 
                complex relationships and trends at a glance.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Interactive data exploration</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Drill-down capability for details</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Multiple visualization formats</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <DeviceTabletIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Device Flexibility</h3>
              <p className="text-text">
                Our mobile-friendly design ensures that critical information is 
                accessible from any device, anywhere, anytime.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Responsive across all devices</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Optimized for touchscreens</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Offline access capabilities</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
        <div className="mt-16">
          <InteractiveReport initialTab="summary" />
        </div>

        <div className="mt-16 text-center">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              href="/contact"
              variant="primary"
              size="lg"
            >
              Schedule a Demo
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveReportingPage; 